import { useState, type ChangeEvent } from 'react';
import CurrencyExchangeOutlined from '@mui/icons-material/CurrencyExchangeOutlined';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Stack,
  styled,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { BaseDialog } from '@/components/shared/base-dialog';
import { HIDE_REFUND_INFO_KEY } from '@/constants/subsidy/local-storage';

type HowRefundWorksDialogProps = {
  showDismissCheckbox?: boolean;
  onClose: () => void;
};

type RefundStep = {
  id: string;
  title: string;
  description: string;
};

const refundSteps: RefundStep[] = [
  {
    id: 'request-approval',
    title: 'Peça aprovação',
    description: 'Escolha o que você quer e peça aprovação antes de comprar.',
  },
  {
    id: 'wait-response',
    title: 'Espere a resposta',
    description: 'Sua empresa vai avisar se você pode seguir com a compra.',
  },
  {
    id: 'buy-send-receipt',
    title: 'Compre e mande o comprovante',
    description:
      'Compre o item, envie a nota fiscal e mande seus dados bancários.',
  },
  {
    id: 'verification',
    title: 'Conferência',
    description:
      'Nós vamos verificar se as informações enviadas estão válidas e legíveis.',
  },
  {
    id: 'payment',
    title: 'Dinheiro na conta',
    description: 'O pagamento cai na sua conta em até 10 dias úteis.',
  },
];

const StepNumberBox = styled(Box)(({ theme }) => ({
  minWidth: 40,
  height: 40,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.success.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const StepNumber = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.success.dark,
}));

const DialogIcon = styled(CurrencyExchangeOutlined)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.success.main,
}));

export const HowRefundWorksDialog = ({
  onClose,
  showDismissCheckbox = false,
}: HowRefundWorksDialogProps) => {
  const navigate = useNavigate();
  const bypassOpen = !!localStorage.getItem(HIDE_REFUND_INFO_KEY);
  const [neverOpenAgain, setNeverOpenAgain] = useState(bypassOpen);

  const handleNewRefund = () => {
    if (neverOpenAgain) {
      localStorage.setItem(HIDE_REFUND_INFO_KEY, 'true');
    } else {
      localStorage.removeItem(HIDE_REFUND_INFO_KEY);
    }
    navigate('/meus-reembolsos/novo');
  };

  const handleCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
    setNeverOpenAgain(event.target.checked);
  };

  return (
    <BaseDialog
      open
      onClose={onClose}
      title="Como funciona o reembolso"
      icon={<DialogIcon />}
      actionsComponent={
        <Box
          sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}
        >
          {!bypassOpen && showDismissCheckbox && (
            <FormControlLabel
              data-testid="never-open-again-checkbox"
              control={
                <Checkbox onChange={handleCheckbox} checked={neverOpenAgain} />
              }
              label={
                <Typography variant="body2">Não mostrar novamente</Typography>
              }
              sx={{ marginRight: 'auto' }}
            />
          )}

          <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
            <Button
              onClick={() => navigate('/meus-reembolsos/minhas-politicas')}
            >
              Ver regras
            </Button>
            <Button onClick={handleNewRefund} variant="contained">
              Pedir reembolso
            </Button>
          </Stack>
        </Box>
      }
    >
      <Stack spacing={3} my={2}>
        {refundSteps.map((step, index) => (
          <Box
            key={step.id}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <StepNumberBox>
              <StepNumber>{index + 1}</StepNumber>
            </StepNumberBox>

            <Box>
              <Typography variant="h3">{step.title}</Typography>
              <Typography>{step.description}</Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </BaseDialog>
  );
};
